/**
 * Pick a payee, or create one by typing its name (TMMAPP-0124 item 15; the same picker as the
 * naming review's Edit, TMMAPP-0120 item 16): the person's payees are listed and typing searches
 * them on the server; a typed name that matches none is its own last option ('Create "…"'); a near
 * miss offers "Did you mean …?" (`payeeCloseness`). `exclude` leaves a payee out (the one the lines
 * are moving from).
 */
import { useEffect, useState } from 'react';
import { Autocomplete, Link, Stack, TextField, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { payeesApi, type Payee } from '@/api/payees';
import { closestName, sameName, searchWord } from './payeeCloseness';

const SEARCH_DEBOUNCE_MS = 200;

/** The choice: an existing payee, or a new one's name (tmm-api reuses an exact-name match). */
export type PayeePick = { payee: Payee } | { name: string };

interface CreateOption {
  create: string;
}
type Option = Payee | CreateOption;

function isCreate(option: Option): option is CreateOption {
  return 'create' in option;
}

interface PayeePickerProps {
  label: string;
  value: PayeePick | null;
  onChange: (pick: PayeePick | null) => void;
  exclude?: number | undefined;
  disabled?: boolean | undefined;
}

export function PayeePicker({ label, value, onChange, exclude, disabled }: PayeePickerProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [typed, setTyped] = useState(false);
  const [options, setOptions] = useState<Payee[]>([]);
  const [candidates, setCandidates] = useState<Payee[]>([]);

  const query = typed ? text.trim() : '';
  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void payeesApi
        .list({ status: 'ACTIVE', ...(query.length > 0 ? { q: query } : {}) })
        .then((rows) => {
          if (!cancelled) setOptions(rows.filter((row) => row.id !== exclude));
        })
        .catch(() => {
          if (!cancelled) setOptions([]);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query, exclude]);

  // "Did you mean": the payees search on the typed name's first word, compared here.
  const word = typed ? searchWord(text) : null;
  useEffect(() => {
    if (word === null) return undefined;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void payeesApi
        .list({ status: 'ACTIVE', q: word })
        .then((rows) => {
          if (!cancelled) setCandidates(rows.filter((row) => row.id !== exclude));
        })
        .catch(() => {
          if (!cancelled) setCandidates([]);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [word, exclude]);

  const typedName = text.trim();
  const known = [...options, ...candidates];
  const matchesExisting = known.some((payee) => sameName(payee.name, typedName));
  const all: Option[] =
    typed && typedName.length > 0 && !matchesExisting
      ? [...options, { create: typedName }]
      : options;
  const picked = value !== null && 'payee' in value ? value.payee : null;
  const didYouMean =
    typed && word !== null && picked === null ? closestName(typedName, candidates) : null;

  return (
    <Stack spacing={0.5}>
      <Autocomplete<Option, false, false, false>
        options={all}
        // The server already searched; never re-filter by the typed text.
        filterOptions={(list) => list}
        getOptionLabel={(option) => (isCreate(option) ? option.create : option.name)}
        getOptionKey={(option) => (isCreate(option) ? 'create' : `payee:${option.id}`)}
        isOptionEqualToValue={(option, current) =>
          !isCreate(option) && !isCreate(current) && option.id === current.id
        }
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <li key={key} {...rest}>
              {isCreate(option) ? t('naming.createPayee', { name: option.create }) : option.name}
            </li>
          );
        }}
        value={picked}
        inputValue={text}
        disabled={disabled}
        onInputChange={(_event, next, reason) => {
          if (reason === 'reset') return;
          setText(next);
          setTyped(true);
          if (reason === 'input') onChange(next.trim().length > 0 ? { name: next.trim() } : null);
        }}
        onChange={(_event, option) => {
          if (option === null) {
            onChange(null);
            return;
          }
          if (isCreate(option)) {
            onChange({ name: option.create });
            return;
          }
          setText(option.name);
          onChange({ payee: option });
        }}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
      {didYouMean !== null && (
        <Typography variant="body2" data-did-you-mean="">
          <Trans
            i18nKey="naming.didYouMean"
            values={{ name: didYouMean.name }}
            components={{
              pick: (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => {
                    setText(didYouMean.name);
                    onChange({ payee: didYouMean });
                  }}
                  sx={{ verticalAlign: 'baseline' }}
                />
              ),
            }}
          />
        </Typography>
      )}
    </Stack>
  );
}
